<template>
  <div class="custom-data__wrapper" :class="extraClass">
    <h2 v-if="label">{{ label }}</h2>
    <div
      class="custom-data__inputs-wrap"
      v-for="(pair, idx) in dataPairs"
      :key="idx"
    >
      <VInput
        label="Key"
        extraClass="custom-data"
        classType="primary"
        :value="pair[0]"
        :name="getTagName(idx, true)"
        :errors="getFakeErrors(idx)"
        :disabled="!editableMap[idx]"
        @input="val => _changeValue(idx, val, true)"
      />

      <VInput
        label="Value"
        extraClass="custom-data"
        classType="primary"
        :value="pair[1]"
        :name="getTagName(idx)"
        :errors="getFakeErrors(idx)"
        :disabled="!editableMap[idx]"
        @input="val => _changeValue(idx, val)"
      />

      <div
        class="custom-data__edit-wrapper"
        title="edit"
        @click="_editHandler(idx)"
        v-if="!editableMap[idx]"
      >
        <i class="fa fa-edit"></i>
      </div>

      <div
        class="custom-data__actions-wrapper"
        :class="{ ['has-errors']: hasEmpty[idx] }"
        v-else
      >
        <div
          class="custom-data__remove-wrapper"
          title="remove"
          @click="removeItem(idx)"
        >
          <i class="fa fa-trash"></i>
        </div>

        <div
          class="custom-data__add-wrapper"
          title="apply"
          @click="applyItem(idx)"
        >
          <i class="fa fa-check"></i>
        </div>
      </div>
      <div class="has-empty" v-if="hasEmpty[idx]">
        Fields can't be empty.
      </div>
    </div>

    <VButton
      type="primary"
      size="sm"
      label="Add new custom data"
      icon="plus"
      @click="addNewPair"
    ></VButton>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import VInput from "@/components/VInput";
import VButton from "@/components/VButton";
import { fromObjectTo2DimArray, from2DimArrayToObject } from "@/utils/util";
import { unset } from "lodash";

export default Vue.component("VCustomDataCreator", {
  props: {
    customData: VueTypes.object,
    label: VueTypes.string,
    extraClass: VueTypes.string
  },

  data() {
    return {
      dataPairs: fromObjectTo2DimArray(this.customData || {}),
      errors: {},
      editableMap: this._initialEditableValues || {},
      hasEmpty: {}
    };
  },

  components: {
    VInput,
    VButton
  },

  methods: {
    _editHandler(idx) {
      Vue.set(this.editableMap, idx, true);
    },

    _changeValue(idx, val, isKey) {
      const item = this.dataPairs[idx];
      Vue.set(item, isKey ? 0 : 1, val);
      if (item[0] && item[1]) {
        Vue.set(this.hasEmpty, idx, false);
      }
    },

    /**
     * Clearing errors and editable
     * @private
     */
    _clearIdx(idx) {
      Vue.delete(this.editableMap, idx);
      Vue.delete(this.errors, idx);
    },

    _setError(idx, isKey, message = "Required") {
      if (!message) {
        unset(this.errors, [`${idx}`, [isKey ? "key" : "value"]]);
      } else {
        Vue.set(this.errors, idx, {
          ...this.errors[idx],
          [isKey ? "key" : "value"]: message
        });
      }
    },

    applyItem(idx) {
      const item = this.dataPairs[idx];

      if (item[0] && item[1]) {
        this._clearIdx(idx);
        Vue.set(this.editableMap, idx, false);
        this.$emit("apply", from2DimArrayToObject(this.dataPairs));
      } else {
        if (!item[0] || !item[1]) {
          Vue.set(this.hasEmpty, idx, true);
        }
      }
    },

    removeItem(idx) {
      this._clearIdx(idx);
      this.dataPairs.splice(idx, 1);
      this.$emit("remove", from2DimArrayToObject(this.dataPairs));
    },

    addNewPair() {
      const lastIdx = this.dataPairs.length;
      Vue.set(this.dataPairs, lastIdx, { "": "" });
      Vue.set(this.editableMap, lastIdx, true);
    },

    getTagName(idx, isKey) {
      return `tag-${isKey ? "key" : "val"}-${idx}`;
    },

    hasErrors(idx) {
      return this.getErrors(idx).length || this.getErrors(idx, true).length;
    }
  },

  computed: {
    _initialEditableValues() {
      const out = {};

      for (let i in this.dataPairs) {
        out[i] = false;
      }

      return out;
    },

    getErrors() {
      return (idx, isKey) => {
        const errObj = this.errors[idx];

        if (!errObj) {
          return [];
        }

        return [isKey ? errObj["key"] : errObj["value"]];
      };
    },

    /**
     * A bit tricky method to make inputs wrapped in red
     * @returns {*}
     */
    getFakeErrors() {
      return idx => (this.hasEmpty[idx] ? [""] : []);
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "custom-data";

.#{$ns} {
  &__wrapper {
    width: 100%;
    text-align: left;

    h2 {
      padding: 10px 0 14px 8px;
      font-size: 15px;
      font-weight: 600;
    }

    .has-empty {
      font-size: 14px;
      color: $danger-color;
    }
  }

  &__inputs-wrap {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr min-content;
    margin-bottom: 10px;
    grid-gap: 10px ;

    .custom-data {
      margin-right: 10px;
    }
  }

  &__actions-wrapper {
    display: flex;
    align-items: flex-end;
    padding-bottom: 5px;

    &.has-errors {
      .#{$ns} {
        &__remove-wrapper,
        &__add-wrapper {
          padding-bottom: 7px;
        }
      }
    }
  }

  &__remove-wrapper,
  &__add-wrapper,
  &__edit-wrapper {
    font-size: 20px;
    cursor: pointer;
    transition: color 0.15s linear;
  }

  &__add-wrapper {
    margin-left: 10px;
    &:hover {
      color: $success-color;
    }
  }

  &__remove-wrapper {
    &:hover {
      color: $danger-color;
    }
  }

  &__edit-wrapper {
    display: flex;
    align-items: flex-end;
    padding-bottom: 5px;
    &:hover {
      color: $primary-color;
    }
  }
}

</style>
